import { useState, useEffect, useRef, useMemo } from 'react';
import { D, st, toArr, apiGet, apiPost, apiDel } from '../constants';

export default function Messages({me}) {
  const myId = me?.user?.id;
  const isOwner = me?.user?.role === 'owner';
  const sender = me?.user?.name || 'Crew';

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const lastTsRef = useRef(null);

  useEffect(() => {
    apiGet('/messages').then(rows => {
      setMessages(rows);
      if (rows.length) lastTsRef.current = rows[rows.length-1].ts;
    }).catch(()=>{});

    const poll = setInterval(() => {
      const since = lastTsRef.current ? `?since=${encodeURIComponent(lastTsRef.current)}` : '';
      apiGet('/messages'+since).then(rows => {
        if (!rows.length) return;
        setMessages(prev => {
          const ids = new Set(prev.map(m=>m.id));
          const fresh = rows.filter(r=>!ids.has(r.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        lastTsRef.current = rows[rows.length-1].ts;
      }).catch(()=>{});
    }, 4000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({behavior:'smooth'}); }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText('');
    try {
      const msg = await apiPost('/messages', { text: t });
      setMessages(prev => [...prev, msg]);
      lastTsRef.current = msg.ts;
    } catch (e) {}
    inputRef.current?.focus();
  };

  const deleteMsg = async (id) => {
    try {
      await apiDel('/messages/'+id);
      setMessages(prev => prev.filter(m=>m.id!==id));
    } catch (e) {}
  };

  const grouped = useMemo(() => {
    const groups = {};
    toArr(messages).forEach(m => {
      const day = new Date(m.ts).toLocaleDateString('en-US', {weekday:'long', month:'long', day:'numeric'});
      if (!groups[day]) groups[day] = [];
      groups[day].push(m);
    });
    return groups;
  }, [messages]);

  const fmtTime = ts => new Date(ts).toLocaleTimeString('en-US', {hour:'numeric', minute:'2-digit'});

  return (
    <div style={{display:'flex', flexDirection:'column', height:'calc(100vh - 120px)'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexShrink:0}}>
        <div style={st.h1}>💬 Messages</div>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <div style={{width:28, height:28, borderRadius:'50%', background:D.accent, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff'}}>{sender.charAt(0).toUpperCase()}</div>
          <span style={{fontSize:13, fontWeight:500, color:D.text}}>{sender}</span>
        </div>
      </div>

      <div style={{flex:1, overflowY:'auto', paddingRight:4, marginBottom:12}}>
        {Object.keys(grouped).length === 0 ? (
          <div style={{textAlign:'center', padding:'60px 20px', color:D.muted}}>
            <div style={{fontSize:40, marginBottom:12}}>💬</div>
            <div style={{fontWeight:500, marginBottom:6}}>No messages yet</div>
            <div style={{fontSize:13}}>Send the first message to the crew.</div>
          </div>
        ) : Object.entries(grouped).map(([day, msgs]) => (
          <div key={day}>
            <div style={{display:'flex', alignItems:'center', gap:10, margin:'16px 0 10px'}}>
              <div style={{flex:1, height:1, background:D.border}}/>
              <div style={{fontSize:11, color:D.muted, whiteSpace:'nowrap'}}>{day}</div>
              <div style={{flex:1, height:1, background:D.border}}/>
            </div>
            {msgs.map(m => {
              const isMe = m.user_id === myId;
              const canDelete = isMe || isOwner;
              return (
                <div key={m.id} style={{display:'flex', flexDirection:isMe?'row-reverse':'row', alignItems:'flex-end', gap:8, marginBottom:8}}>
                  <div style={{width:30, height:30, borderRadius:'50%', background:isMe?D.accent:D.border, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:isMe?'#fff':D.text, flexShrink:0}}>
                    {(m.sender||'?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{maxWidth:'70%'}}>
                    {!isMe && <div style={{fontSize:11, color:D.muted, marginBottom:3, marginLeft:2}}>{m.sender}</div>}
                    <div style={{background:isMe?D.accent:D.card, color:isMe?'#fff':D.text, borderRadius:isMe?'14px 14px 4px 14px':'14px 14px 14px 4px', padding:'9px 13px', fontSize:13, lineHeight:1.5, border:isMe?'none':`1px solid ${D.border}`, wordBreak:'break-word', whiteSpace:'pre-wrap'}}>
                      {m.text}
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:6, marginTop:3, justifyContent:isMe?'flex-end':'flex-start'}}>
                      <span style={{fontSize:10, color:D.muted}}>{fmtTime(m.ts)}</span>
                      {canDelete && <button onClick={()=>deleteMsg(m.id)} style={{background:'none', border:'none', color:D.muted, cursor:'pointer', fontSize:11, padding:0, lineHeight:1}}>Delete</button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>

      <div style={{flexShrink:0, display:'flex', gap:8, alignItems:'flex-end', padding:'10px 12px', background:D.card, border:`1px solid ${D.border}`, borderRadius:12}}>
        <textarea
          ref={inputRef}
          style={{...st.input, flex:1, resize:'none', height:44, lineHeight:1.5, fontSize:14, border:'none', background:'transparent', padding:'8px 0', outline:'none'}}
          placeholder="Message the crew…"
          value={text}
          onChange={e=>setText(e.target.value)}
          onKeyDown={e=>{ if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <button style={{...st.btn('primary'), marginLeft:0, padding:'10px 18px', fontSize:14, flexShrink:0, borderRadius:10}} onClick={send}>Send</button>
      </div>
      <div style={{fontSize:10, color:D.muted, marginTop:5, textAlign:'center'}}>Enter to send · Shift+Enter for new line</div>
    </div>
  );
}
